import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ChevronLeft, FileText, Video, File, Check, Lock, DollarSign } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Course, CourseContent } from "@/types/courseTypes";
import { Separator } from "@/components/ui/separator";
import { collection, doc, getDoc, getDocs, getFirestore, query, setDoc, where } from "firebase/firestore";
import { BookOpen, Clock, Info } from "lucide-react";
import { useFirebase } from "@/contexts/FirebaseContext";
import { useAuth } from "@/contexts/AuthContext";
import { Enrollment, createEnrollment, getCourseById } from "@/utils/firestoreService";
import { PaymentDialog } from "@/components/payment/PaymentDialog";

const TheoryCourseView = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [course, setCourse] = useState<Course | null>(null);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeContent, setActiveContent] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [completedContents, setCompletedContents] = useState<string[]>([]);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [enrollmentId, setEnrollmentId] = useState("");
  const { currentUser } = useFirebase();
  const { user } = useAuth();

  useEffect(() => {
    const loadCourseData = async () => {
      if (!courseId) return;
      
      setLoading(true);
      try {
        // Load course from Firebase
        const fetchedCourse = await getCourseById(courseId);
        
        if (fetchedCourse && fetchedCourse.type === "theory") {
          setCourse(fetchedCourse);
          
          // Set first content as active if available
          if (fetchedCourse.contents && fetchedCourse.contents.length > 0) {
            setActiveContent(fetchedCourse.contents[0].id);
          }
        } else {
          toast({
            title: "Error",
            description: "Course not found or not a theory course.",
            variant: "destructive",
          });
          navigate("/student-dashboard/courses");
          return;
        }

        // Check enrollment status if user is logged in
        if (currentUser?.uid) {
          const enrollmentsRef = collection(getFirestore(), "enrollments");
          const q = query(
            enrollmentsRef,
            where("courseId", "==", courseId),
            where("userId", "==", currentUser.uid)
          );
          const enrollmentSnapshot = await getDocs(q);
          
          if (!enrollmentSnapshot.empty) {
            const enrollmentData = {
              id: enrollmentSnapshot.docs[0].id,
              ...enrollmentSnapshot.docs[0].data()
            } as Enrollment;
            
            // Set enrollment with correct shape
            setEnrollment(enrollmentData);
            
            // Load progress from localStorage
            const storedProgress = localStorage.getItem(`course_progress_${courseId}_${currentUser.uid}`);
            if (storedProgress) {
              const progress = JSON.parse(storedProgress);
              setCompletedContents(progress.completedContents || []);
            }
          }
        }
      } catch (error) {
        console.error("Error loading course:", error);
        toast({
          title: "Error",
          description: "Failed to load course details.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadCourseData();
  }, [courseId, navigate, toast, currentUser]);

  useEffect(() => {
    // Calculate progress percentage
    if (course?.contents && course.contents.length > 0 && currentUser?.uid) {
      const progressPercentage = (completedContents.length / course.contents.length) * 100;
      setProgress(progressPercentage);
      
      // Save progress to localStorage
      const progressData = {
        courseId,
        userId: currentUser.uid,
        completedContents,
        lastUpdated: new Date().toISOString()
      };
      localStorage.setItem(`course_progress_${courseId}_${currentUser.uid}`, JSON.stringify(progressData));
    }
  }, [completedContents, course, courseId, currentUser]);

  const handleMarkAsComplete = (contentId: string) => {
    if (!completedContents.includes(contentId)) {
      const updatedCompletedContents = [...completedContents, contentId];
      setCompletedContents(updatedCompletedContents);
      
      toast({
        title: "Progress Updated",
        description: "This section has been marked as complete",
      });
    }
  };

  const handleEnroll = async () => {
    if (!currentUser?.uid) {
      toast({
        title: "Authentication Required",
        description: "Please log in to enroll in this course",
        variant: "destructive"
      });
      navigate("/login");
      return;
    }

    if (!course) return;
    
    setProcessingPayment(true);
    
    try {
      toast({
        title: "Processing Enrollment",
        description: "Please wait while we process your enrollment..."
      });
      
      const studentName = user?.firstName && user?.lastName 
        ? `${user.firstName} ${user.lastName}`
        : currentUser.displayName || "Student";
      
      // Create the enrollment with the user's ID
      const enrollmentData: Omit<Enrollment, 'id' | 'enrolledAt' | 'updatedAt'> = {
        userId: currentUser.uid,
        studentName: studentName,
        studentEmail: currentUser.email || user?.email || "student@example.com",
        courseId: courseId!,
        courseName: course.title,
        courseType: course.type,
        status: "pending",
        enrollmentStatus: "pending",
        paymentStatus: "pending",
        instructorId: "",
        instructorName: "",
        instructorEmail: "",
        startDate: new Date().toISOString(),
        preferredTime: "09:00", // Default time for theory courses
        drivingExperience: "Not Applicable", // Not relevant for theory courses
        amount: Math.max(course.price, 1.00), // Ensure minimum amount of £1.00 for Stripe
        currency: "GBP",
        isPartialPayment: false,
        amountPaid: 0,
        fullCoursePrice: Math.max(course.price, 1.00), // Ensure minimum amount of £1.00 for Stripe
        remainingHours: 0
      };
      
      // Create enrollment in Firestore
      const createdEnrollment = await createEnrollment(enrollmentData);
      
      // Set the enrollment ID and show payment dialog
      setEnrollmentId(createdEnrollment.id);
      setShowPaymentDialog(true);
      
    } catch (error) {
      console.error("Error enrolling in course:", error);
      toast({
        title: "Enrollment Failed",
        description: "There was an error processing your enrollment. Please try again.",
        variant: "destructive"
      });
    } finally {
      setProcessingPayment(false);
    }
  };

  const handlePaymentComplete = (status: 'pending' | 'paid', method: 'cash' | 'card') => {
    setShowPaymentDialog(false);
    toast({
      title: "Enrollment Successful",
      description: method === 'cash' 
        ? "Your booking is pending payment. Please complete the payment at our office."
        : "You now have access to the full course content",
    });
    navigate("/student-dashboard/my-enrollments");
  };

  const getContentTypeIcon = (type: string) => {
    switch (type) {
      case "text":
        return <FileText className="h-5 w-5 text-blue-500" />;
      case "video":
        return <Video className="h-5 w-5 text-red-500" />;
      case "file":
      case "quiz":
      default:
        return <File className="h-5 w-5 text-green-500" />;
    }
  };

  const ContentView = ({ content }: { content: CourseContent }) => {
    switch (content.type) {
      case "text":
        return (
          <div className="mt-4 prose max-w-none">
            <p>{content.content}</p>
          </div>
        );
      case "video":
        return (
          <div className="mt-4">
            <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
              <div className="text-center p-6">
                <Video className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">Video content would be embedded here</p>
                <p className="text-sm text-blue-500 mt-2">{content.url}</p>
              </div>
            </div>
          </div>
        );
      case "file":
        return (
          <div className="mt-4">
            <div className="border rounded-lg p-4 flex items-center">
              <File className="h-8 w-8 text-gray-400 mr-3" />
              <div>
                <p className="font-medium">{content.title}</p>
                <p className="text-sm text-gray-500">{content.description}</p>
                <a href={content.url} className="text-sm text-blue-500 hover:underline" target="_blank" rel="noopener noreferrer">
                  View File
                </a>
              </div>
            </div>
          </div>
        );
      case "quiz":
        return (
          <div className="mt-4">
            <div className="border rounded-lg p-4">
              <div className="flex items-center mb-4">
                <FileText className="h-6 w-6 text-purple-500 mr-2" />
                <h3 className="font-medium">Quiz: {content.title}</h3>
              </div>
              <p className="text-gray-600">{content.description}</p>
              <Button className="mt-4" variant="outline">Start Quiz</Button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="student">
        <div className="flex items-center justify-center h-64">
          <p>Loading course...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!course) {
    return (
      <DashboardLayout role="student">
        <div className="flex flex-col items-center justify-center h-64">
          <p className="text-lg">Course not found</p>
          <Button className="mt-4" onClick={() => navigate("/student-dashboard/courses")}>
            Back to Courses
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="student">
      <div className="mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate("/student-dashboard/courses")}
          className="mb-4"
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back to Courses
        </Button>
        
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{course.title}</h1>
            <p className="text-gray-600 mt-2">{course.description}</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Course Overview</CardTitle>
              <CardDescription>Key information about this course</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div>
                      <h3 className="font-medium">Duration</h3>
                      <p className="text-sm text-gray-600">{course.duration} days access</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <BookOpen className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div>
                      <h3 className="font-medium">Course Content</h3>
                      <p className="text-sm text-gray-600">{course.contents.length} lessons included</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div>
                      <h3 className="font-medium">What You'll Learn</h3>
                      <ul className="text-sm text-gray-600 list-disc pl-4 mt-1">
                        <li>Understanding road signs and markings</li>
                        <li>Traffic rules and regulations</li>
                        <li>Vehicle safety procedures</li>
                        <li>Preparing for your theory test</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {!enrollment ? (
            <Card>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div>
                    <h2 className="text-xl font-bold">Ready to Start Learning?</h2>
                    <p className="text-gray-600 mt-1">
                      Enroll now to get immediate access to all course materials
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center">
                      <span className="text-xl font-bold">£{Math.max(course.price, 1.00).toFixed(2)}</span>
                    </div>
                    <div className="space-y-4">
                      <Button 
                        onClick={handleEnroll} 
                        size="lg" 
                        disabled={processingPayment}
                      >
                        {processingPayment ? "Processing..." : "Enroll Now"}
                      </Button>

                      {showPaymentDialog && enrollmentId && (
                        <PaymentDialog
                          isOpen={showPaymentDialog}
                          onClose={() => setShowPaymentDialog(false)}
                          enrollmentId={enrollmentId}
                          amount={Math.max(course?.price || 0, 1.00)} // Ensure minimum amount of £1.00 for Stripe
                          onPaymentComplete={handlePaymentComplete}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="mb-6">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex-1">
                    <h3 className="font-medium mb-2">Your Progress</h3>
                    <div className="flex items-center gap-4">
                      <Progress value={progress} className="h-2 flex-1" />
                      <span className="text-sm font-medium">{Math.round(progress)}%</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      {completedContents.length} of {course.contents?.length || 0} sections completed
                    </p>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium mb-2">Course Duration</h3>
                    <p>{course.duration} days access</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      
      {/* Only show course content after enrollment */}
      {enrollment ? (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1 order-2 lg:order-1">
            <Card>
              <CardHeader>
                <CardTitle>Course Content</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {course.contents.map((content) => (
                    <div 
                      key={content.id}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                        activeContent === content.id ? "bg-gray-50" : ""
                      }`}
                      onClick={() => enrollment ? setActiveContent(content.id) : null}
                    >
                      <div className="flex items-start">
                        <div className="mr-3 mt-0.5">
                          {enrollment ? (
                            completedContents.includes(content.id) ? (
                              <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                                <Check className="h-3 w-3 text-white" />
                              </div>
                            ) : (
                              getContentTypeIcon(content.type)
                            )
                          ) : (
                            <Lock className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                        <div>
                          <h4 className={`font-medium text-sm ${!enrollment ? "text-gray-500" : ""}`}>
                            {content.title}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                            {content.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="lg:col-span-3 order-1 lg:order-2">
            <Card>
              <CardHeader>
                <CardTitle>
                  {enrollment && activeContent 
                    ? course.contents.find(c => c.id === activeContent)?.title || "Course Content" 
                    : "Course Preview"}
                </CardTitle>
                {enrollment && activeContent && (
                  <CardDescription>
                    {course.contents.find(c => c.id === activeContent)?.description}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                {enrollment ? (
                  activeContent ? (
                    <div>
                      <ContentView 
                        content={course.contents.find(c => c.id === activeContent)!} 
                      />
                      <div className="mt-8 pt-4 border-t flex justify-between">
                        <Button
                          variant="outline"
                          onClick={() => {
                            const currentIndex = course.contents.findIndex(c => c.id === activeContent);
                            if (currentIndex > 0) {
                              setActiveContent(course.contents[currentIndex - 1].id);
                            }
                          }}
                          disabled={course.contents.findIndex(c => c.id === activeContent) === 0}
                        >
                          Previous
                        </Button>
                        
                        <div className="flex gap-2">
                          {!completedContents.includes(activeContent || "") && (
                            <Button 
                              onClick={() => handleMarkAsComplete(activeContent!)}
                              variant="outline"
                            >
                              Mark as Complete
                            </Button>
                          )}
                          
                          <Button
                            onClick={() => {
                              const currentIndex = course.contents.findIndex(c => c.id === activeContent);
                              if (currentIndex < course.contents.length - 1) {
                                setActiveContent(course.contents[currentIndex + 1].id);
                              }
                            }}
                            disabled={course.contents.findIndex(c => c.id === activeContent) === course.contents.length - 1}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p>Select a section from the content list to begin.</p>
                    </div>
                  )
                ) : null}
              </CardContent>
            </Card>
          </div>
        </div>
      ) : (
        <Card className="mt-6">
          <CardContent className="p-6">
            <div className="text-center py-12">
              <Info className="h-12 w-12 text-blue-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium">Preview Not Available</h3>
              <p className="text-gray-500 mt-2 max-w-md mx-auto">
                Enroll in this course to access all content and track your progress.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </DashboardLayout>
  );
};

export default TheoryCourseView;
